import { Loading, Toggle, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useUpdateClientProfileMutation } from "@/redux/apis/clientProfile/clientProfileApi";
import { ClientProfileNotifications } from "@/redux/apis/clientProfile/types";
import { addToast } from "@/utils";

export interface NotificationAlertsCardProps {
  value: ClientProfileNotifications;
  accountId?: string;
}

type NotificationAlertRow = {
  field: keyof ClientProfileNotifications;
  label: string;
  description: string;
  /** Renders a divider below the row (splits the Email group from the SMS group). */
  divider?: boolean;
};

const ROWS: NotificationAlertRow[] = [
  {
    field: "send_email_on_own_bookings",
    label: "Email Notification",
    description: "Send Email Notifications for Own bookings (created/deleted/updated)?",
  },
  {
    field: "send_email_on_guest_bookings",
    label: "Email Notification",
    description: "Send Email Notifications for Guest bookings (created/deleted/updated)?",
    divider: true,
  },
  {
    field: "send_sms_reminders",
    label: "SMS Notification",
    description: "Send SMS reminders (Reminders / OpChange notifications)?",
  },
  {
    field: "send_sms_rate_trip",
    // Figma labels this row "Email Notification", but it is an SMS preference — labelled SMS to match the group.
    label: "SMS Notification",
    description: "Send SMS rate trip messages?",
  },
];

export const NotificationAlertsCard = ({ value, accountId }: NotificationAlertsCardProps) => {
  const [updateClientProfile, { isLoading }] = useUpdateClientProfileMutation();

  const handleToggle = (field: keyof ClientProfileNotifications, checked: boolean) => {
    // No local state: the PATCH invalidates ClientProfile, so the refetched profile drives `checked`.
    updateClientProfile({ accountId, notifications: { [field]: checked } })
      .unwrap()
      .then(() => addToast("success", "Notification preferences updated"))
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  return (
    <div className="relative flex flex-col rounded-lg border border-neutral-gray bg-white p-5 @container">
      {isLoading && <Loading />}
      <Typography variant="title" className="pb-2 text-neutral-silver-gray">
        Notification Alerts
      </Typography>

      <div className="flex flex-col">
        {ROWS.map(({ field, label, description, divider }) => {
          const checked = value[field];

          return (
            <div key={field} className={divider ? "border-b border-neutral-mid-gray pb-6" : undefined}>
              <div className="grid grid-cols-1 items-start gap-2 py-4 @[560px]:grid-cols-[220px_minmax(0,360px)] @[560px]:gap-x-12 @[560px]:gap-y-0">
                <div className="flex flex-col gap-0.5">
                  <Typography variant="paragraph" className="font-semibold text-neutral-black">
                    {label}
                  </Typography>
                  <Typography variant="small-regular" className="text-neutral-dark-gray">
                    {description}
                  </Typography>
                </div>
                <div className="flex items-center gap-4">
                  <Toggle
                    size="sm"
                    aria-label={description}
                    checked={checked}
                    onChange={(next) => handleToggle(field, next)}
                  />
                  <Typography variant="action" className="text-neutral-dark-gray">
                    {checked ? "Yes" : "No"}
                  </Typography>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
